import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import "../rules/rules.css";
import "./manageTeams.css";
import { selectAdminToken } from "../../sessionSlice";
import { getTeamSettings } from "../../functions/getTeamSettings";
import { updateTeamSettings } from "../../functions/updateTeamSettings";
import { getJoinedUsers } from "../../functions/getJoinedUsers";
import { getTeams } from "../../functions/getTeams";
import { createTeam } from "../../functions/createTeam";

const TEAM_SIZE_OPTIONS = [1, 2, 3, 4, 5];
const REFRESH_INTERVAL_MS = 6000;

const ManageTeams = () => {
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);

  const [autoJoin, setAutoJoin] = useState(true);
  const [teamSize, setTeamSize] = useState(1);
  const [savingSettings, setSavingSettings] = useState(false);

  const [joinedUsers, setJoinedUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [creatingTeam, setCreatingTeam] = useState(false);

  const loadSettings = useCallback(async () => {
    if (!adminToken) return;
    try {
      const data = await getTeamSettings({ token: adminToken });
      setAutoJoin(Boolean(data.autoJoin));
      setTeamSize(data.teamSize || 1);
    } catch (e) {
      console.error("Failed to load team settings:", e);
    }
  }, [adminToken]);

  const loadLists = useCallback(async () => {
    if (!adminToken) return;
    try {
      const [usersRes, teamsRes] = await Promise.all([
        getJoinedUsers({ token: adminToken }),
        getTeams({ token: adminToken }),
      ]);
      setJoinedUsers(usersRes.users || []);
      setTeams(teamsRes.teams || []);
    } catch (e) {
      console.error("Failed to load joined users/teams:", e);
    }
  }, [adminToken]);

  useEffect(() => {
    loadSettings();
    loadLists();
  }, [loadSettings, loadLists]);

  useEffect(() => {
    const timer = window.setInterval(loadLists, REFRESH_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [loadLists]);

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      await updateTeamSettings({ autoJoin, teamSize, token: adminToken });
      Swal.fire("Saved", "Team settings updated.", "success");
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to save settings.", "error");
    } finally {
      setSavingSettings(false);
    }
  };

  const toggleUserSelected = (userId) => {
    setSelectedUserIds((prev) => {
      if (prev.includes(userId)) {
        return prev.filter((id) => id !== userId);
      }
      if (prev.length >= 5) {
        Swal.fire("Limit Reached", "A team can have at most 5 members.", "warning");
        return prev;
      }
      return [...prev, userId];
    });
  };

  const handleCreateTeam = async () => {
    if (!selectedUserIds.length) return;
    setCreatingTeam(true);
    try {
      await createTeam({ userIds: selectedUserIds, token: adminToken });
      setSelectedUserIds([]);
      await loadLists();
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to create team.", "error");
    } finally {
      setCreatingTeam(false);
    }
  };

  return (
    <div className="rules-page-layout">
      <div className="back-button-holder">
        <button className="back-button" onClick={() => navigate("/admin/")}>
          <i className="fa-solid fa-arrow-left"></i> Back
        </button>
      </div>

      <div className="rules-page-new">
        <div className="admin-tabs-holder manage-teams-scope">
          <div className="admin-tab-container manage-teams-settings-container">
            <div className="admin-tab-title">
              <i className="fa-solid fa-people-group"></i> &nbsp; Team Settings
            </div>
            <div className="form-group-v1 manage-teams-toggle-row">
              <label>Auto Join</label>
              <label className="manage-teams-switch">
                <input
                  type="checkbox"
                  checked={autoJoin}
                  onChange={(e) => setAutoJoin(e.target.checked)}
                />
                <span className="manage-teams-switch-slider" />
              </label>
            </div>
            {autoJoin && (
              <div className="form-group-v1">
                <label>Team Size</label>
                <select
                  className="rules-grid-select"
                  value={teamSize}
                  onChange={(e) => setTeamSize(Number(e.target.value))}
                >
                  {TEAM_SIZE_OPTIONS.map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>
            )}
            <div className="rules-action-holder">
              <button
                className="manage-teams-btn manage-teams-btn--primary"
                onClick={handleSaveSettings}
                disabled={savingSettings}
              >
                {savingSettings ? "Saving..." : "Save Settings"}
              </button>
            </div>
          </div>

          <div className="admin-tab-container manage-teams-joined-container">
            <div className="admin-tab-title">
              <i className="fa-solid fa-user-group"></i> &nbsp; Joined Users
            </div>
            {joinedUsers.length === 0 ? (
              <p className="manage-teams-empty">No users have joined yet.</p>
            ) : (
              <div className="manage-teams-table-wrap">
                <table className="manage-teams-table">
                  <thead>
                    <tr>
                      <th></th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {joinedUsers.map((u) => (
                      <tr
                        key={u.userId}
                        className={selectedUserIds.includes(u.userId) ? "is-selected" : ""}
                      >
                        <td>
                          <input
                            type="checkbox"
                            disabled={u.assigned}
                            checked={selectedUserIds.includes(u.userId)}
                            onChange={() => toggleUserSelected(u.userId)}
                          />
                        </td>
                        <td>{u.name || u.userId}</td>
                        <td>{u.email}</td>
                        <td>
                          {u.assigned ? (
                            <span className="manage-teams-badge">
                              Team {u.teamNumber} ({u.role})
                            </span>
                          ) : (
                            <span className="manage-teams-badge manage-teams-badge--pending">
                              Unassigned
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="rules-action-holder">
              <button
                className="manage-teams-btn manage-teams-btn--primary"
                onClick={handleCreateTeam}
                disabled={!selectedUserIds.length || creatingTeam}
              >
                {creatingTeam
                  ? "Creating..."
                  : `Create Team (${selectedUserIds.length} selected)`}
              </button>
            </div>
          </div>

          <div className="admin-tab-container manage-teams-teams-container">
            <div className="admin-tab-title">
              <i className="fa-solid fa-trophy"></i> &nbsp; Teams
            </div>
            {teams.length === 0 ? (
              <p className="manage-teams-empty">No teams created yet.</p>
            ) : (
              <ul className="manage-teams-team-list">
                {teams.map((t) => (
                  <li className="manage-teams-team-row" key={t.teamId}>
                    <span className="manage-teams-team-row-title">Team {t.teamNumber}</span>
                    <span className="manage-teams-team-row-members">
                      {t.members.map((m) => (
                        <span className="manage-teams-team-row-member" key={m.userId}>
                          {m.name || m.userId}
                          <span
                            className={`manage-teams-badge ${
                              m.role === "captain" ? "" : "manage-teams-badge--pending"
                            }`}
                          >
                            {m.role}
                          </span>
                        </span>
                      ))}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageTeams;
